// src/components/legal/PomocPubliczna.js

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Code, Shield, FileCode, Lock, Cpu, Scale, CheckCircle, ArrowRight, ChevronDown, ChevronUp, DollarSign, FilePenLine   } from 'lucide-react';
import Trust from './Trust';
import ContactForm from './ContactForm';

// Add font import
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const PomocPubliczna = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const benefits = [
    "Głęboka znajomość przepisów unijnych i krajowych",
    "Doświadczenie w reprezentacji w sporach",
    "Proaktywne podejście do minimalizacji ryzyka",
    "Zabezpieczenie Twoich interesów"
  ];

  const faqs = [
    {
      question: "Co to jest pomoc publiczna?",
      answer: "Pomoc publiczna to wsparcie udzielane przez państwo lub z zasobów publicznych przedsiębiorstwom, które prowadzi do zakłócenia lub groźby zakłócenia konkurencji na rynku wewnętrznym UE. Podlega ścisłym regulacjom unijnym."
    },
    {
      question: "Jakie są konsekwencje naruszenia zasad pomocy publicznej?",
      answer: "Naruszenie zasad pomocy publicznej może skutkować koniecznością zwrotu nielegalnie otrzymanej pomocy wraz z odsetkami, co może stanowić poważne obciążenie dla przedsiębiorstwa."
    },
    {
      question: "Czy dotacje unijne zawsze stanowią pomoc publiczną?",
      answer: "Większość dotacji unijnych dla przedsiębiorstw jest formą pomocy publicznej i musi być zgodna z unijnymi regulacjami w tym zakresie. Istnieją jednak pewne wyłączenia lub programy, które nie są kwalifikowane jako pomoc publiczna, np. gdy wsparcie ma charakter ogólny i nie wpływa na konkurencję."
    }
  ];

  const handleContactScroll = () => {
    const contactSection = document.querySelector('#kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 animate-float">
            <DollarSign  className="w-32 h-32 text-violet-600" />
          </div>
          <div className="absolute bottom-20 left-20 animate-float animation-delay-2000">
            <FilePenLine   className="w-24 h-24 text-violet-500" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Pomoc Publiczna i Fundusze Unijne
            </h1>
            <p className="text-2xl lg:text-3xl text-white mb-12 font-semibold">
              Szynkowski Legal zapewnia kompleksowe doradztwo prawne, gwarantując pełną zgodność Twoich działań z przepisami krajowymi i unijnymi, minimalizując ryzyko nieprawidłowości.
            </p>
            
        
            <div className="flex justify-center">
              <button 
                onClick={handleContactScroll}
                className="group flex items-center gap-3 text-white text-xl lg:text-2xl font-medium hover:text-violet-500 transition-all duration-300 uppercase"
              >
                <span>Bezpłatna konsultacja</span>
                <ArrowRight className="w-6 h-6 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-6 md:mb-8 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Kompleksowe wsparcie prawne w zakresie pomocy publicznej i funduszy unijnych
            </h2>
            <div className="space-y-6 text-lg text-gray-300">
              <p>
                Korzystanie z funduszy unijnych i innych form pomocy publicznej wiąże się z koniecznością przestrzegania skomplikowanych regulacji prawnych. Nieprawidłowości mogą skutkować koniecznością zwrotu środków wraz z odsetkami.
              </p>
              <p>
                Nasza kancelaria specjalizuje się w prawie pomocy publicznej, zapewniając bezpieczeństwo prawne na każdym etapie – od pozyskania do rozliczenia.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-600/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Dlaczego warto wybrać Szynkowski Legal w obszarze pomocy publicznej?
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-violet-600 flex-shrink-0 mt-1" />
                  <span className="text-lg text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-xl text-gray-300 mb-8">
                Zapewnij zgodność prawną swojego projektu. Skontaktuj się z nami, aby porozmawiać o bezpieczeństwie Twoich funduszy!
              </p>
              <button 
                onClick={handleContactScroll}
                className="group flex items-center gap-3 text-white text-xl lg:text-2xl font-medium hover:text-violet-500 transition-all duration-300 uppercase mx-auto"
              >
                <span>Bezpłatna konsultacja</span>
                <ArrowRight className="w-6 h-6 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl lg:text-5xl font-black text-white mb-12 text-center uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
            FAQ: Prawo Pomocy Publicznej i Fundusze Unijne
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="backdrop-blur-md bg-white/5 rounded-xl border border-white/10 overflow-hidden transition-all duration-300 hover:border-violet-600/30"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between group"
                >
                  <h3 className="text-lg font-semibold text-white pr-4">
                    {faq.question}
                  </h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-violet-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-violet-600 transition-colors flex-shrink-0" />
                  )}
                </button>
                
                <div className={`transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'} overflow-hidden`}>
                  <div className="px-6 pb-4">
                    <p className="text-gray-400">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <Trust />

      {/* Contact Form */}
      <ContactForm />

      {/* Animation styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(10deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default PomocPubliczna;